import { useCallback } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHotelRoutePaths, useHotelBookingRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { AccommodationListingDTO, AccommodationsConsoleState } from '../AccommodationsConsole.types';
import { AccommodationCatalogUnitOccupancyInput } from '@graphql/generated';

/**
 * Search parameters for accommodation redirect URLs
 */
type AccommodationSearchParams = Readonly<{
  rooms?: string;
  checkIn?: string;
  checkOut?: string;
  utm_source: string;
  utm_campaign: string;
  skipIfNoSearchParams?: string;
  utm_medium: 'web' | 'email' | 'text';
}>;

export const createUtmParametersForAccommodationsConsolePage = (): AccommodationSearchParams => {
  return { utm_medium: 'web', utm_campaign: 'map_view_see_availability', utm_source: 'website_accommodations_console_page' };
};

/**
 * Formats a room's occupancy into the URL parameter format
 * @example
 * // returns "a2:c5:c7" for a room with 2 adults and 2 children aged 5 and 7
 * formatRoomParameter({ adults: 2, childrenAges: [5, 7] })
 */
const formatRoomParameter = (room: AccommodationCatalogUnitOccupancyInput): string => {
  const adultsParam = `a${room.adults}`;
  const childrenParam = room.childrenAges?.length ? `:c${room.childrenAges.join(':c')}` : '';
  return `${adultsParam}${childrenParam}`;
};

export const useAccommodationRedirectUrl = (state: AccommodationsConsoleState) => {
  const { eventHandle } = useEventInfo();
  const hotelRoute = useHotelRoutePaths();
  const { buildPath, roomblock } = useHotelBookingRoutePaths();

  return useCallback(
    (listing: AccommodationListingDTO) => {
      if (!eventHandle) {
        return undefined;
      }

      const paramsData: AccommodationSearchParams = {
        utm_medium: 'web',
        utm_source: 'website_accommodations_console_page',
        utm_campaign: 'see_availability',
        ...(state.searchDates && {
          checkIn: state.searchDates.checkin,
          checkOut: state.searchDates.checkout,
        }),
        ...(state.occupancies?.occupancies?.length && {
          rooms: state.occupancies.occupancies.map(formatRoomParameter).join(','),
        }),
        ...(state.searchDates &&
          state.occupancies?.occupancies?.length && {
            skipIfNoSearchParams: 'true',
          }),
      };

      if (listing.type === 'roomBlock') {
        if (!listing.id) {
          return undefined;
        }
        return buildPath(eventHandle, roomblock, listing.id);
      }

      if (listing.isPickedByCouple) {
        if (!listing.id) {
          return undefined;
        }
        return hotelRoute.accommodationPlace.buildRelativeUrl(eventHandle, listing.id, paramsData);
      }

      if (!listing.joyPlaceId) {
        return undefined;
      }

      return hotelRoute.hotelDetails.buildRelativeUrl(eventHandle, listing.joyPlaceId, paramsData);
    },
    [buildPath, eventHandle, hotelRoute.accommodationPlace, hotelRoute.hotelDetails, roomblock, state.occupancies?.occupancies, state.searchDates],
  );
};
